<template>
	<div>
		<c-auth-start
			v-if="currentStep === NextStep.AuthStart"
			ref="authStart"
			:is-signup="isSignup"
			:auth-result="authResult"
			:required-organization="requiredOrganizationName"
			:required-user="requiredUser"
			:is-invitation="isInvitation"
			:is-not-a-member="isNotAMember"
			:button-status="authStartButtonStatus"
			:auto-social-signup="autoSocialSignup"
			@email-authentication="emailAuthentication"
			@social-authentication="socialAuthentication"
			@redirect-to-signup="handleRedirectToSignup"
			@email-input="handleEmailInput"
		/>
		<c-password-login
			v-if="currentStep === NextStep.PasswordLogin"
			:auth-result="authResult"
			:login-status="loginStatus"
			@password-login="loginWithPassword"
			@forgot-password="handleForgotPassword"
			@reset-login="handleResetLogin"
		>
		</c-password-login>
		<c-user-signup
			v-if="currentStep === NextStep.Signup"
			:auth-result="authResult"
			@signup="handleSignup"
		>
		</c-user-signup>
		<c-password-reset
			v-if="currentStep === NextStep.ResetPassword"
			:code="resetPasswordCode"
			:invalid-code="invalidResetCode"
			@reset-password="handleResetPassword"
			@forgot-password="handleForgotPassword"
			@reset-login="handleResetLogin"
		>
			<template #captcha>
				<slot name="captcha"></slot>
			</template>
		</c-password-reset>
		<device-verification
			v-if="currentStep === NextStep.DeviceVerification"
			ref="deviceVerificationRoot"
			:email="authResult.UserEmail"
			:status="verificationStatus"
			@device-verified="handleValidateDeviceVerificationCode"
			@resend-device-verification="handleResendDeviceVerificationCode"
		/>
		<multifactor-verification
			v-else-if="currentStep === NextStep.MultifactorLogin"
			ref="multifactorVerificationRoot"
			:mfa-result="authResult"
			:status="mfaVerificationStatus"
			:show-remember-device="showRememberDevice"
			@validate-multifactor="handleValidateMulifactorCode"
			@send-mfa-backup-code="handleSendMfaBackupCode"
		/>
		<c-access-lost
			v-if="currentStep === NextStep.AccessLost"
			:auth-result="authResult"
		/>
		<c-portal-terms-of-service
			v-if="currentStep === NextStep.TermsOfService"
			:auth-result="authResult"
			@continue="handleAcceptPortalTerms"
		/>
	</div>
</template>

<script lang="ts">
	import CAuthStart from './AuthStart.vue';
	import CPasswordLogin from './PasswordLogin.vue';
	import CUserSignup from './UserSignup.vue';
	import DeviceVerification from 'src/components/auth/DeviceVerification.vue';
	import MultifactorVerification from 'src/components/auth/MultifactorVerification.vue';
	import CAccessLost from 'src/components/auth/AccessLost.vue';
	import CPortalTermsOfService from 'src/components/auth/PortalTermsOfService.vue';
	import notify from '@cognitoforms/shared-components/src/utilities/notify';
	import CPasswordReset from './PasswordReset.vue';
	import { defineComponent, onMounted, ref, watch } from 'vue';
	import { useRoute } from 'vue-router/composables';
	import { NextStep } from '@cognitoforms/types/server-types/services/infrastructure/authentication/next-step';
	import {
		initiateEmailAuthentication, initiateSocialAuthentication, passwordLogin, loginWithDeviceVerification, signupWithDeviceVerification,
		resendDeviceVerificationCode, forgotPassword, resetPassword, validateResetCode, signupWithEmail, multifactorVerification, sendMultifactorBackupCode,
		acceptPortalTermsOfService
	} from '@cognitoforms/api/services/auth-service';

	import type { AuthResult } from '@cognitoforms/types/server-types/services/infrastructure/authentication/auth-result';
	import type { PasswordLoginResult } from '@cognitoforms/types/server-types/services/infrastructure/authentication/password-login-result';
	import type { RestartAuthenticationResult } from '@cognitoforms/types/server-types/services/infrastructure/authentication/restart-authentication-result';
	import { AuthenticationMethodType } from '@cognitoforms/types/server-types/model/authentication-method-type';
	import type { ResetPasswordResult } from '@cognitoforms/types/server-types/services/infrastructure/authentication/reset-password-result';
	import type { DeviceVerificationResult } from '@cognitoforms/types/server-types/services/infrastructure/authentication/device-verification-result';
	import type { ButtonStatus } from '@cognitoforms/shared-components/src/components/interaction/StatusButton.vue';
	import type { MultifactorResult } from '@cognitoforms/types/server-types/services/infrastructure/authentication/multifactor-result';
	import { isWithinFrame } from '@cognitoforms/utils/utilities';
	import type { AccessLostResult } from '@cognitoforms/types/server-types/services/infrastructure/authentication/access-lost-result';
	import type { TermsOfServiceResult } from '@cognitoforms/types/server-types/services/infrastructure/authentication/terms-of-service-result';

	interface EmailLoginEvent {
		password: string,
		rememberMe: boolean,
	};

	interface EmailSignupEvent {
		name: string,
		password: string
	}

	type AuthResultType =
		& AuthResult
		& PasswordLoginResult
		& ResetPasswordResult
		& DeviceVerificationResult
		& RestartAuthenticationResult
		& MultifactorResult
		& AccessLostResult
		& TermsOfServiceResult;

	export default defineComponent({
		name: 'AuthContainer',
		components: {
			CAuthStart,
			CPasswordLogin,
			DeviceVerification,
			CPasswordReset,
			CUserSignup,
			MultifactorVerification,
			CAccessLost,
			CPortalTermsOfService
		},
		emits: ['title-changed'],
		setup(_, { emit }) {
			const route = useRoute();
			const isSignup = ref<boolean>(route.name === 'signup');

			// Signup related query params
			const source = route.query.source as string;
			const details = route.query.details as string;

			const currentStep = ref<NextStep>(NextStep.AuthStart);
			const authResult = ref<AuthResultType>(null);
			const returnUrl = route.query.returnUrl ? route.query.returnUrl + (route.hash || '') : null;
			const rememberMe = ref<boolean>(false);
			const showRememberDevice = ref<boolean>(false);

			// fallback to code if it is present.
			// common in older saved org-scoped routes
			const requiredOrganization = (route.query.organizationCode || route.query.code) as string;
			const requiredOrganizationName = (route.query.destinationOrganization || route.query.requiredOrganization) as string;

			let requiredUser = route.query.requiredUser;
			if (!requiredUser && returnUrl) {
				const requiredUserInReturnUrl = returnUrl.match(/requiredUser=([^&]+)/);
				if (requiredUserInReturnUrl)
					requiredUser = decodeURIComponent(requiredUserInReturnUrl[1]);
			}

			const isInvitation = route.query.isInvitation === 'true';
			const isNotAMember = route.query.onlyOrgMembers === 'true';

			const resetTitle = () => {
				if (requiredOrganization)
					emit('title-changed', null);
				else if (isSignup.value)
					emit('title-changed', 'Sign Up');
				else
					emit('title-changed', 'Log In');
			};

			watch(authResult, (newAuthResult) => {
				if (!newAuthResult) {
					resetTitle();
					return;
				}

				if (authResult.value.NextStep === NextStep.Success) {
					const redirectUrl = authResult.value.RedirectUrl;
					window.location.assign(redirectUrl);
				}
				else {
					// Only update current step if the user is not being logged in so that the page does not change
					currentStep.value = authResult.value.NextStep;
					switch (currentStep.value) {
					case NextStep.AccessLost:
						emit('title-changed', 'Access Restricted');
						break;
					case NextStep.MultifactorLogin:
						emit('title-changed', 'Two-Factor Authentication');
						break;
					case NextStep.DeviceVerification:
						emit('title-changed', 'Verification Code');
						break;
					case NextStep.ResetPassword:
						emit('title-changed', 'Reset Password');
						break;
					case NextStep.Signup:
						emit('title-changed', 'Sign Up');
						break;
					default:
						resetTitle();
						break;
					}
				}
			});

			const handleResetLogin = () => {
				currentStep.value = NextStep.AuthStart;
				authResult.value = null;
				resetTitle();
			};

			const getThumbprint = () => {
				if (localStorage)
					return localStorage.getItem('c-user');
			};

			const parseAndStoreThumbprint = () => {
				if (!localStorage || !authResult.value)
					return;

				const authToken = authResult.value.AuthToken;
				if (!authToken || !authToken.includes('.'))
					return;

				const payload = authToken.split('.')[1];
				const decoded = JSON.parse(atob(payload));
				localStorage.setItem('c-user', decoded.thumbprint);
			};

			const authStartButtonStatus = ref<ButtonStatus>('default');
			const emailAuthentication = async(email: string) => {
				try {
					authStartButtonStatus.value = 'validating';
					authResult.value = await initiateEmailAuthentication(email, getThumbprint(), returnUrl, requiredOrganization, requiredUser);
					parseAndStoreThumbprint();
					authStartButtonStatus.value = 'default';

					// Email is associated with an existing account
					if (isSignup.value && authResult.value.NextStep === NextStep.PasswordLogin)
						isSignup.value = false;
				}
				catch {
					authStartButtonStatus.value = 'error';
				}
			};

			const socialAuthentication = async(token: string, authMethodType: AuthenticationMethodType) => {
				authResult.value = await initiateSocialAuthentication(token, authMethodType, getThumbprint(), returnUrl, requiredOrganization, requiredUser, source, details, isSignup.value);
				parseAndStoreThumbprint();

				if (authResult.value.NextStep === NextStep.MultifactorLogin)
					showRememberDevice.value = true;
			};

			// Ensure authResult is cleared when email input changes
			const handleEmailInput = () => {
				authResult.value = null;
			};

			const loginStatus = ref<ButtonStatus>('default');
			const loginWithPassword = async(event: EmailLoginEvent) => {
				loginStatus.value = 'validating';
				try {
					authResult.value = await passwordLogin(event.password, event.rememberMe, authResult.value.AuthToken);
					if (authResult.value.PasswordError)
						loginStatus.value = 'error';
					else {
						loginStatus.value = 'success';
						rememberMe.value = event.rememberMe;
					}
				}
				catch {
					loginStatus.value = 'error';
					handleResetLogin();
				}
			};

			const handleForgotPassword = async () => {
				if (currentStep.value === NextStep.PasswordLogin)
					emit('title-changed', 'Forgot Password');
				try {
					await forgotPassword(authResult.value.AuthToken);
				}
				catch {
					handleResetLogin();
				}
			};

			const autoSocialSignup = ref<boolean>(false);
			const handleRedirectToSignup = () => {
				emit('title-changed', 'Sign Up');

				if (authResult.value.AuthenticationMethod !== AuthenticationMethodType.Email) {
					isSignup.value = true;
					autoSocialSignup.value = true;
				}
				else
					currentStep.value = NextStep.Signup;
			};

			const handleSignup = async(event: EmailSignupEvent) => {
				authResult.value = await signupWithEmail(authResult.value.UserEmail, event.password, event.name, authResult.value.AuthToken, source, details);
			};

			const resetPasswordCode = route.query.resetCode as string;
			const invalidResetCode = ref<boolean>(false);

			onMounted(async () => {
				resetTitle();
				const isResetPasswordLink = !!route.query.resetCode;
				if (isResetPasswordLink) {
					// Validate code if exists
					authResult.value = await validateResetCode(resetPasswordCode);
					invalidResetCode.value = authResult.value.InvalidCode;
					emit('title-changed', 'Reset Password');
				}

				const forwardedAuthResult = !!route.query.authResult;
				if (forwardedAuthResult) {
					const parsedResult = JSON.parse(route.query.authResult as string);
					authResult.value = structuredClone(parsedResult);

					if (authResult.value.NextStep === NextStep.MultifactorLogin)
						showRememberDevice.value = true;
				}
			});

			const handleResetPassword = async (password: string) => {
				authResult.value = await resetPassword(resetPasswordCode, password, getThumbprint());
				parseAndStoreThumbprint();
			};

			// Device verification
			const deviceVerificationRoot = ref<InstanceType<typeof DeviceVerification>>(null);
			const verificationStatus = ref<ButtonStatus>('default');

			const handleValidateDeviceVerificationCode = async (code: string) => {
				verificationStatus.value = 'validating';

				try {
					let res;
					if (isSignup.value || requiredOrganization)
						res = await signupWithDeviceVerification(code, authResult.value.AuthToken, rememberMe.value);
					else
						res = await loginWithDeviceVerification(code, authResult.value.AuthToken, rememberMe.value);

					if (res.VerificationCodeError)
						verificationStatus.value = 'error';
					else
						verificationStatus.value = 'success';
					authResult.value = res;
				}
				catch {
					verificationStatus.value = 'error';
				}
			};

			const handleResendDeviceVerificationCode = () => {
				resendDeviceVerificationCode(authResult.value.AuthToken).then(() => {
					notify('We\'re sending you another code now.', 'success');
					// set focus on the mfa input field
					deviceVerificationRoot.value.mfaInput.root.querySelector('input').focus();
				}).catch(() => {
					notify('An error has occurred.', 'error');
				});
			};

			// Multifactor verifications
			const multifactorVerificationRoot = ref<InstanceType<typeof MultifactorVerification>>(null);
			const mfaVerificationStatus = ref<ButtonStatus>('default');

			const handleValidateMulifactorCode = (code: string, rememberThisDevice: boolean) => {
				mfaVerificationStatus.value = 'validating';

				// Determines what remember checkbox we should get our value from
				const rememberValue = showRememberDevice.value ? rememberThisDevice : rememberMe.value;

				multifactorVerification(code, authResult.value.AuthToken, rememberValue).then((res: MultifactorResult) => {
					mfaVerificationStatus.value = 'default';
					authResult.value = res as AuthResultType;
					if (res.NextStep === NextStep.Success)
						mfaVerificationStatus.value = 'success';
					else if (res.InvalidCode)
						mfaVerificationStatus.value = 'error';
				});
			};

			const handleSendMfaBackupCode = () => {
				sendMultifactorBackupCode(authResult.value.AuthToken);

				notify('We\'re sending you another code now.', 'success');

				// set focus on the mfa input field
				multifactorVerificationRoot.value.mfaInput.root.querySelector('input').focus();
			};

			const handleAcceptPortalTerms = async () => {
				authResult.value = await acceptPortalTermsOfService(authResult.value.AuthToken);
			};

			const data = {
				// Shared
				isSignup,
				route,
				currentStep,
				NextStep, // to reference enum in template
				authResult,

				// AuthStart
				authStartButtonStatus,
				emailAuthentication,
				handleEmailInput,
				handleRedirectToSignup,
				socialAuthentication,
				requiredOrganizationName,
				requiredUser,
				isInvitation,
				isNotAMember,
				autoSocialSignup,

				// PasswordLogin
				loginStatus,
				loginWithPassword,
				handleResetLogin,
				handleForgotPassword,

				// Signup
				handleSignup,

				// PasswordReset
				resetPasswordCode,
				invalidResetCode,
				handleResetPassword,

				// DeviceVerification
				deviceVerificationRoot,
				verificationStatus,
				handleValidateDeviceVerificationCode,
				handleResendDeviceVerificationCode,

				// Multifactor
				handleValidateMulifactorCode,
				handleSendMfaBackupCode,
				mfaVerificationStatus,
				showRememberDevice,
				multifactorVerificationRoot,

				// Portal Terms of Service
				handleAcceptPortalTerms
			};

			const showDevLogin = process.env.NODE_ENV === 'development' && !isWithinFrame() && currentStep.value === NextStep.AuthStart;
			if (showDevLogin) {
				const authStart = ref<InstanceType<typeof CAuthStart> | null>(null);
				watch(authStart, () => {
					if (showDevLogin && authStart.value)
						import('src/components/auth/dev-login').then(x => x.enableDevLogin(authStart.value, requiredOrganization, returnUrl));
				});

				return {
					...data,
					authStart
				};
			}

			return data;
		}
	});
</script>

<style lang="scss" src="@cognitoforms/styles/app/common/_auth.scss"></style>